import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Info, CheckCircle, Edit, Save, X, ChevronDown, ChevronUp, User } from 'lucide-react';

interface ScoringAgentBasicInfoProps {
  agent: {
    id: string;
    name: string;
    description: string;
    teamName: string;
    teamId: string;
    status: 'live' | 'draft';
    enabled: boolean;
    createdDate: string;
    lastModified: string;
  };
  teams?: Array<{ id: string; name: string }>;
  onSave: (data: {
    name: string;
    description: string;
    teamId: string;
    status: 'live' | 'draft';
    enabled: boolean;
  }) => void;
}

const ScoringAgentBasicInfo: React.FC<ScoringAgentBasicInfoProps> = ({
  agent,
  teams = [],
  onSave
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [formData, setFormData] = useState({
    name: agent.name,
    description: agent.description,
    teamId: agent.teamId,
    status: agent.status,
    enabled: agent.enabled
  });

  const handleSave = () => {
    onSave(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: agent.name,
      description: agent.description,
      teamId: agent.teamId,
      status: agent.status,
      enabled: agent.enabled
    });
    setIsEditing(false);
  };

  const selectedTeam = teams.find(team => team.id === formData.teamId);

  return (
    <Collapsible open={!isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)}>
      <Card>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="hover:bg-muted/30 transition-colors">
            <CardTitle className="flex items-center justify-between text-left">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <User className="h-5 w-5 text-primary" />
                </div>
                Basic Information
              </div>
              <div className="flex items-center gap-2">
                {!isCollapsed && !isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                {!isCollapsed && isEditing && (
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={handleCancel}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave}>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                  </div>
                )}
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {!isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Agent Name</label>
                  <p className="text-base font-medium">{agent.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="flex items-center gap-2">
                    <Badge variant={agent.status === 'live' ? 'default' : 'secondary'}>
                      {agent.status === 'live' ? (
                        <CheckCircle className="h-3 w-3 mr-1" />
                      ) : null}
                      {agent.status.toUpperCase()}
                    </Badge>
                    <Badge variant={agent.enabled ? 'default' : 'secondary'}>
                      {agent.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Team</label>
                  <p className="text-base">{agent.teamName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Agent ID</label>
                  <p className="text-base font-mono text-sm">{agent.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created Date</label>
                  <p className="text-base">{agent.createdDate}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Modified</label>
                  <p className="text-base">{agent.lastModified}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="text-base mt-1">{agent.description}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="agent-name">Agent Name</Label>
                    <Input
                      id="agent-name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter agent name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="team-select">Team</Label>
                    <Select value={formData.teamId} onValueChange={(value) => setFormData(prev => ({ ...prev, teamId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="status-select">Status</Label>
                    <Select value={formData.status} onValueChange={(value: 'live' | 'draft') => setFormData(prev => ({ ...prev, status: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="enabled-switch"
                      checked={formData.enabled}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
                    />
                    <Label htmlFor="enabled-switch">Agent Enabled</Label>
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter agent description"
                    className="min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Agent ID</label>
                    <p className="text-base font-mono text-sm">{agent.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created Date</label>
                    <p className="text-base">{agent.createdDate}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default ScoringAgentBasicInfo;